import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getInventoryTables, 
  getAvailableBrands,
  bulkUpdateInventoryTable,
  downloadInventoryExcel,
  InventoryTableData 
} from '../services/inventoryApi';
import InventoryTable1 from '../components/inventory/InventoryTable1';
import InventoryTable2 from '../components/inventory/InventoryTable2';
import InventoryTable3 from '../components/inventory/InventoryTable3';
import InventorySearch from '../components/inventory/InventorySearch';
// Removed react-icons import to avoid TypeScript issues

const Inventory = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'tables' | 'search'>('tables');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inventoryData, setInventoryData] = useState<InventoryTableData | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBrand, setSelectedBrand] = useState('AQUAFIT');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check if user has admin/manager privileges
  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadBrands();
    loadInventoryData();
  }, [selectedDate, selectedBrand]);

  const loadBrands = async () => {
    try {
      const response = await getAvailableBrands();
      setAvailableBrands(response.brands || []);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const loadInventoryData = async () => {
    setLoading(true);
    try {
      const data = await getInventoryTables({
        date: selectedDate,
        brand1: selectedBrand
      });
      setInventoryData(data);
      setMessage(null);
    } catch (error: any) {
      console.error('Error loading inventory data:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load inventory data'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
  };

  const handleBrandChange = (value: string) => {
    setSelectedBrand(value);
  };

  const handleTable1Update = async (updates: any[]) => {
    if (!canEdit) return;
    
    setSaving(true);
    try {
      const response = await bulkUpdateInventoryTable({
        date: selectedDate,
        updates: updates.map(update => ({
          type: 'purchase_sales' as const,
          productNo: update.productNo,
          purchase: update.purchase,
          sales: update.sales,
          rate: update.rate
        }))
      });

      if (response.success) {
        setMessage({
          type: 'success',
          text: `Successfully updated ${response.successfulUpdates} items`
        });
        await loadInventoryData(); // Reload data
      }
    } catch (error: any) {
      console.error('Error updating table 1:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update purchase/sales data'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTable3Update = async (updates: any[]) => {
    if (!canEdit) return;
    
    setSaving(true);
    try {
      const response = await bulkUpdateInventoryTable({
        date: selectedDate,
        updates: updates.map(update => ({
          type: 'rate' as const,
          productId: update.productId,
          rate: update.rate
        }))
      });

      if (response.success) {
        setMessage({
          type: 'success',
          text: `Successfully updated ${response.successfulUpdates} rates`
        });
        await loadInventoryData(); // Reload data
      }
    } catch (error: any) {
      console.error('Error updating table 3:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update rates'
      });
    } finally {
      setSaving(false);
    }
  };

  // Filter inventory data based on search query
  const getFilteredData = () => {
    if (!inventoryData || !searchQuery.trim()) return inventoryData;

    const query = searchQuery.toLowerCase();
    const filterRows = (rows: any[]) => 
      rows.filter(item => 
        item.productNo?.toLowerCase().includes(query) ||
        item.productName?.toLowerCase().includes(query)
      );

    return {
      ...inventoryData,
      table1: {
        ...inventoryData.table1,
        rows: filterRows(inventoryData.table1.rows)
      },
      table2: {
        ...inventoryData.table2,
        rows: filterRows(inventoryData.table2.rows)
      },
      table3: {
        ...inventoryData.table3,
        rows: filterRows(inventoryData.table3.rows)
      },
    };
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await downloadInventoryExcel({
        date: selectedDate
      });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory_${selectedDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage({
        type: 'success',
        text: 'Excel file downloaded successfully'
      });
    } catch (error: any) {
      console.error('Error downloading Excel:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to download Excel file'
      });
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Inventory Management</h1>
        <p className="text-gray-600">Manage inventory tables and product data</p>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Brand Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand
            </label>
            <select
              value={selectedBrand}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableBrands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Product
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by product code or name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <button
            onClick={loadInventoryData}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <span className={`mr-2 ${loading ? 'animate-spin' : ''}`}>🔄</span>
            Refresh
          </button>
          <button
            onClick={handleDownloadExcel}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <span className="mr-2">📥</span>
            Excel
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('tables')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tables'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Inventory Tables
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'search'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Search Products
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'tables' && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : inventoryData ? (
            <>
              {/* Search Results Info */}
              {searchQuery && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    Showing results for: <strong>"{searchQuery}"</strong> in {selectedBrand}
                    <button
                      onClick={() => setSearchQuery('')}
                      className="ml-4 text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear search
                    </button>
                  </p>
                </div>
              )}

              {/* Table 1 - Detailed Transaction Table */}
              <InventoryTable1
                data={getFilteredData()?.table1 || inventoryData.table1}
                canEdit={canEdit}
                onUpdate={handleTable1Update}
                saving={saving}
              />

              {/* Table 2 - Simple Stock Table */}
              <InventoryTable2
                data={getFilteredData()?.table2 || inventoryData.table2}
              />

              {/* Table 3 - Stock with Rates and Amounts */}
              <InventoryTable3
                data={getFilteredData()?.table3 || inventoryData.table3}
                canEdit={canEdit}
                onUpdate={handleTable3Update}
                saving={saving}
              />

              {/* Summary */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{inventoryData.summary.totalProducts}</div>
                    <div className="text-sm text-gray-600">Total Products</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{inventoryData.summary.aquafitProducts}</div>
                    <div className="text-sm text-gray-600">{selectedBrand} Products</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{(getFilteredData()?.table1.rows || inventoryData.table1.rows).length}</div>
                    <div className="text-sm text-gray-600">Displayed Products</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{inventoryData.summary.entriesWithData}</div>
                    <div className="text-sm text-gray-600">With Inventory Data</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No inventory data available</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'search' && (
        <InventorySearch
          selectedDate={selectedDate}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};

export default Inventory;


